import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#E05A47", "#2563EB", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];

export default function Reports() {
  const [report, setReport] = useState("sales-daily");
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/sales").then((r) => setSales(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const dailySales = React.useMemo(() => {
    const map = {};
    sales.forEach((s) => {
      const d = new Date(s.date).toISOString().slice(0, 10);
      map[d] = (map[d] || 0) + s.total;
    });
    return Object.entries(map).sort().slice(-14).map(([date, total]) => ({ date: new Date(date).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" }), total }));
  }, [sales]);

  const bycategory = React.useMemo(() => {
    const map = {};
    sales.forEach((s) => s.items.forEach((it) => {
      const p = products.find((x) => x.id === it.product_id);
      const cat = p?.category || "Diğer";
      map[cat] = (map[cat] || 0) + it.total;
    }));
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [sales, products]);

  const bycustomer = React.useMemo(() => {
    return [...customers].sort((a, b) => b.total_sales - a.total_sales).slice(0, 10);
  }, [customers]);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Raporlar</h1>
          <p className="text-sm text-muted-foreground">İşletme performans raporları</p>
        </div>
        <Select value={report} onValueChange={setReport}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="sales-daily">Günlük Satış</SelectItem>
            <SelectItem value="sales-category">Kategori Bazlı Satış</SelectItem>
            <SelectItem value="sales-customer">Müşteri Bazlı Satış</SelectItem>
            <SelectItem value="debts">Müşteri Borçları</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {report === "sales-daily" && (
        <Card className="p-6">
          <h3 className="font-bold mb-4">Son 14 Günlük Satış</h3>
          <div className="h-80">
            <ResponsiveContainer>
              <BarChart data={dailySales}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <Bar dataKey="total" fill="hsl(11 71% 58%)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {report === "sales-category" && (
        <Card className="p-6">
          <h3 className="font-bold mb-4">Kategori Bazlı Satış Dağılımı</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="h-80">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={bycategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(x) => x.name}>
                    {bycategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtTL(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Kategori</TableHead><TableHead className="text-right">Tutar</TableHead></TableRow></TableHeader>
              <TableBody>{bycategory.map((c) => (<TableRow key={c.name}><TableCell>{c.name}</TableCell><TableCell className="text-right num font-semibold">{fmtTL(c.value)}</TableCell></TableRow>))}</TableBody>
            </Table>
          </div>
        </Card>
      )}

      {report === "sales-customer" && (
        <Card className="p-4">
          <Table>
            <TableHeader><TableRow><TableHead>Müşteri</TableHead><TableHead className="text-right">Toplam Alışveriş</TableHead><TableHead className="text-right">Ödeme</TableHead><TableHead className="text-right">Kalan Borç</TableHead></TableRow></TableHeader>
            <TableBody>{bycustomer.map((c) => (<TableRow key={c.id}><TableCell className="font-semibold">{c.name}</TableCell><TableCell className="text-right num">{fmtTL(c.total_sales)}</TableCell><TableCell className="text-right num text-emerald-500">{fmtTL(c.total_paid)}</TableCell><TableCell className="text-right num font-semibold text-primary">{fmtTL(c.balance)}</TableCell></TableRow>))}</TableBody>
          </Table>
        </Card>
      )}

      {report === "debts" && (
        <Card className="p-4">
          <Table>
            <TableHeader><TableRow><TableHead>Müşteri</TableHead><TableHead className="text-right">Kalan Borç</TableHead></TableRow></TableHeader>
            <TableBody>{customers.filter((c) => c.balance > 0).map((c) => (<TableRow key={c.id}><TableCell className="font-semibold">{c.name}</TableCell><TableCell className="text-right num font-bold text-primary">{fmtTL(c.balance)}</TableCell></TableRow>))}</TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
